import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MdTimeline, MdChecklist, MdDescription, MdSearch, MdBalance } from 'react-icons/md';
import { FaFolderOpen, FaUsers, FaUserShield } from 'react-icons/fa';
import { createApiClient } from '../../api/client';
import { useCaseContext } from '../context/CaseContext';
import { getPreferredLang, SECTION_INFO_I18N, CONTINUE_PROMPT_I18N } from '../../utils/sectionConfirmations';
import '../mobile.css';

// Same icons, colors, and count-based status dot as the desktop DashboardButton
// (src/components/InvestigationDashboard.js), just laid out for a 2-column mobile grid.
const BUTTONS = [
  { endpoint: 'events', title: 'Case Brief', icon: MdTimeline, countOf: (fir) => fir?.eventsAndTimeline?.length || 0 },
  { endpoint: 'tasks', title: 'SOPs', icon: MdChecklist, countOf: (fir) => fir?.urgentTasks?.length || 0 },
  { endpoint: 'evidences', title: 'Evidences', icon: FaFolderOpen, countOf: (fir) => Object.keys(fir?.evidenceList || {}).length },
  { endpoint: 'witnesses', title: 'Witnesses', icon: FaUsers, countOf: (fir) => fir?.witnessList?.length || 0 },
  { endpoint: 'interrogations', title: 'Accused', icon: FaUserShield, countOf: (fir) => fir?.accusedList?.length || 0 },
  { endpoint: 'statements', title: 'Statements', icon: MdDescription, countOf: (fir) => fir?.investigation?.responses?.length || 0 },
  { endpoint: 'discrepancies', title: 'Discrepancies', icon: MdSearch, countOf: (fir) => (fir?.investigation?.contradictions?.length || 0) + (fir?.investigation?.inconsistencies?.length || 0) },
  { endpoint: 'chargesheet', title: 'Chargesheet', icon: MdBalance, countOf: (fir) => (fir?.caseDiary?.bucketFileName ? 1 : 0) },
];

const DashboardButton = ({ title, Icon, count, onClick, loading, disabled }) => {
  const isProcessed = Number(count) > 0;
  const isDisabled = Boolean(loading || disabled);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isDisabled}
      style={{
        width: '100%',
        boxSizing: 'border-box',
        backgroundColor: '#f4f8fb',
        padding: '30px 18px',
        borderRadius: '12px',
        border: 'none',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: disabled && !loading ? 0.65 : 1,
      }}
      aria-busy={loading ? 'true' : 'false'}
      aria-disabled={isDisabled ? 'true' : 'false'}
    >
      <div style={{ height: 40, display: 'grid', placeItems: 'center' }}>
        {loading ? <div className="m-spinner" style={{ borderTopColor: '#094268' }} /> : <Icon size={32} color="#094268" />}
      </div>

      <div style={{ marginTop: 12, fontSize: 16, fontWeight: 600, color: '#094268' }}>
        {title}
      </div>

      <div style={{ marginTop: 6, minHeight: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {isProcessed ? (
          <span
            aria-label="Completed"
            title="Completed"
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: '#22c55e',
              boxShadow: '0 0 0 2px rgba(34,197,94,0.15)',
              display: 'inline-block',
            }}
          />
        ) : (
          <span style={{ width: 10, height: 10, display: 'inline-block' }} />
        )}
      </div>
    </button>
  );
};

const MobileInvestigationDashboard = ({ API_URL }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { fullFIR, setFullFIR } = useCaseContext();

  const initialFir = location.state?.fir;
  const firId = fullFIR?.id || initialFir?.id;

  const [loadingButton, setLoadingButton] = useState(null);
  const [isLoadingFIR, setIsLoadingFIR] = useState(false);
  const [error, setError] = useState(null);
  const [confirmSection, setConfirmSection] = useState({ open: false, endpoint: null, title: '', text: '' });

  useEffect(() => {
    // Show the summary data immediately, but always fetch the authoritative full
    // record too (like desktop does) - the case-list summary passed via navigation
    // state doesn't reliably include eventsAndTimeline/urgentTasks/etc, so the
    // green "data available" dots would be wrong if we only relied on it.
    if (initialFir && !fullFIR) setFullFIR(initialFir);
    if (!fullFIR) {
      setIsLoadingFIR(true);
      getFullFIR().finally(() => setIsLoadingFIR(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getFullFIR = async () => {
    if (!firId) return null;
    try {
      const api = createApiClient(API_URL);
      const response = await api.get(`/fir/${firId}`);
      setFullFIR(response.data);
      return response.data;
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate('/m/login');
        return null;
      }
      setError('Error fetching case record. Please try again.');
      return null;
    }
  };

  // Mirrors desktop's isSectionGenerated (src/components/InvestigationDashboard.js) -
  // whether an endpoint already has generated data, so we skip the confirmation
  // popup and navigate straight to the results.
  const isSectionGenerated = (data, endpoint) => {
    if (!data) return false;
    switch (endpoint) {
      case 'events':
        return Array.isArray(data.eventsAndTimeline) && data.eventsAndTimeline.length > 0;
      case 'tasks':
        return Array.isArray(data.urgentTasks) && data.urgentTasks.length > 0;
      case 'evidences':
        return data.evidenceList && typeof data.evidenceList === 'object' && Object.keys(data.evidenceList).length > 0;
      case 'witnesses':
        return Array.isArray(data.witnessList) && data.witnessList.length > 0;
      case 'interrogations':
        return Array.isArray(data.accusedList) && data.accusedList.length > 0;
      case 'statements':
        return Array.isArray(data?.investigation?.responses) && data.investigation.responses.length > 0;
      case 'discrepancies': {
        const c = data?.investigation?.contradictions?.length ?? 0;
        const i = data?.investigation?.inconsistencies?.length ?? 0;
        return c + i > 0;
      }
      case 'chargesheet':
        return Boolean(data?.caseDiary?.bucketFileName);
      default:
        return false;
    }
  };

  // Mirrors desktop's handleSectionClick: if the section is already generated, go
  // straight there; otherwise show a localized "this will take about a minute"
  // confirmation before kicking off the AI analysis.
  const handleSectionClick = async (endpoint) => {
    let data = fullFIR;
    if (!data) data = await getFullFIR();
    if (!data) return;

    if (isSectionGenerated(data, endpoint)) {
      callApi(endpoint);
      return;
    }

    const lang = getPreferredLang();
    const dict = SECTION_INFO_I18N[lang] || SECTION_INFO_I18N.en;
    const cfg = dict[endpoint] || SECTION_INFO_I18N.en[endpoint];

    if (!cfg) {
      callApi(endpoint);
      return;
    }

    setConfirmSection({ open: true, endpoint, title: cfg.title, text: cfg.text });
  };

  const closeSectionConfirm = () => setConfirmSection({ open: false, endpoint: null, title: '', text: '' });

  const confirmSectionAndProceed = () => {
    const endpoint = confirmSection.endpoint;
    closeSectionConfirm();
    if (endpoint) callApi(endpoint);
  };

  const callApi = async (endpoint) => {
    setLoadingButton(endpoint);
    setError(null);

    try {
      let data = fullFIR;
      if (!data) data = await getFullFIR();
      if (!data) return;

      if (endpoint === 'events' && Array.isArray(data.eventsAndTimeline) && data.eventsAndTimeline.length > 0) {
        navigate('/m/fir/timeline', { state: { fir: data, section: 'timeline' } });
        return;
      }
      if (endpoint === 'tasks' && Array.isArray(data.urgentTasks) && data.urgentTasks.length > 0) {
        navigate('/m/fir/tasks', { state: { fir: data, section: 'tasks' } });
        return;
      }
      if (endpoint === 'evidences' && data.evidenceList && Object.keys(data.evidenceList).length > 0) {
        navigate('/m/fir/evidences', { state: { fir: data, section: 'evidences' } });
        return;
      }
      if (endpoint === 'witnesses' && Array.isArray(data.witnessList) && data.witnessList.length > 0) {
        navigate('/m/fir/witnesses', { state: { fir: data, section: 'witnesses' } });
        return;
      }
      if (endpoint === 'interrogations') {
        navigate('/m/fir/accused', { state: { fir: data, section: 'accused' } });
        return;
      }
      if (endpoint === 'statements') {
        const responses = data.investigation?.responses?.length > 0 ? data.investigation.responses : [];
        if (responses.length === 0) {
          navigate('/m/fir/statements/new', { state: { id: data.id } });
          return;
        }
        navigate('/m/fir/statements', { state: { fir: data, section: 'statements' } });
        return;
      }
      if (endpoint === 'discrepancies') {
        navigate('/m/fir/discrepancies', { state: { fir: data, section: 'discrepancies' } });
        return;
      }
      if (endpoint === 'chargesheet') {
        if (data.caseDiary?.bucketFileName) {
          navigate('/m/fir/chargesheet/generated', { state: { fir: data } });
        } else {
          navigate('/m/fir/casediary/new', { state: { fir: data } });
        }
        return;
      }

      const api = createApiClient(API_URL);
      const response = await api.get(`/fir/${endpoint}/${firId}`);
      if (response.status === 200) {
        data = response.data;
        setFullFIR(data);
      }

      switch (endpoint) {
        case 'events':
          navigate('/m/fir/timeline', { state: { fir: data, section: 'timeline' } });
          break;
        case 'tasks':
          navigate('/m/fir/tasks', { state: { fir: data, section: 'tasks' } });
          break;
        case 'evidences':
          navigate('/m/fir/evidences', { state: { fir: data, section: 'evidences' } });
          break;
        case 'witnesses':
          navigate('/m/fir/witnesses', { state: { fir: data, section: 'witnesses' } });
          break;
        case 'discrepancies':
          navigate('/m/fir/discrepancies', { state: { fir: data, section: 'discrepancies' } });
          break;
        default:
          break;
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate('/m/login');
        return;
      }
      setError('Request failed. Please try again.');
    } finally {
      setLoadingButton(null);
    }
  };

  const complainantName = fullFIR?.complainant?.name || initialFir?.complainant?.name || '';
  const caseType = fullFIR?.caseType || initialFir?.caseType || '';

  return (
    <div>
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '18px', fontWeight: 700, color: '#333' }}>{complainantName}</div>
        <div style={{ fontWeight: 'bold', color: '#8C1007' }}>{caseType || 'Matrimonial'}</div>
      </div>

      {isLoadingFIR ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 0' }}>
          <div className="m-spinner" style={{ width: '32px', height: '32px', borderColor: '#e0e8ee', borderTopColor: '#094268' }} />
          <p style={{ marginTop: '12px', color: '#555' }}>Loading case...</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {BUTTONS.map(({ endpoint, title, icon, countOf }) => (
            <DashboardButton
              key={endpoint}
              title={title}
              Icon={icon}
              count={countOf(fullFIR)}
              onClick={() => handleSectionClick(endpoint)}
              loading={loadingButton === endpoint}
              disabled={loadingButton !== null && loadingButton !== endpoint}
            />
          ))}
        </div>
      )}

      {confirmSection.open && (
        <div className="m-popup-overlay">
          <div className="m-popup-container">
            <h3>{confirmSection.title}</h3>
            <p style={{ textAlign: 'justify' }}>
              {confirmSection.text}
              <br /><br />
              {CONTINUE_PROMPT_I18N[getPreferredLang()] || CONTINUE_PROMPT_I18N.en}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={closeSectionConfirm} className="m-pill-btn" style={{ color: '#094268', fontWeight: 600 }}>Cancel</button>
              <button className="m-button-primary" style={{ width: 'auto', padding: '8px 14px' }} onClick={confirmSectionAndProceed}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="m-popup-overlay">
          <div className="m-popup-container">
            <h3>Error</h3>
            <p>{error}</p>
            <button className="m-button-primary" onClick={() => setError(null)}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileInvestigationDashboard;
